package songwritersnotebook.app.screens

import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Text
import androidx.compose.material3.TopAppBar
import androidx.compose.material3.TopAppBarDefaults
import androidx.compose.runtime.Composable
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun PrivacyPolicyScreen(onBack: () -> Unit) {
    Scaffold(
        topBar = {
            TopAppBar(
                title = { Text("Privacy Policy", fontWeight = FontWeight.Bold) },
                navigationIcon = {
                    IconButton(onClick = onBack) {
                        Icon(Icons.AutoMirrored.Filled.ArrowBack, contentDescription = "Go back")
                    }
                },
                colors = TopAppBarDefaults.topAppBarColors(containerColor = MaterialTheme.colorScheme.background)
            )
        }
    ) { paddingValues ->
        Column(
            modifier = Modifier
                .padding(paddingValues)
                .padding(16.dp)
                .fillMaxSize()
                .verticalScroll(rememberScrollState())
        ) {
            Text(
                "Privacy Policy for Songwriter's Notebook",
                style = MaterialTheme.typography.headlineSmall,
                fontWeight = FontWeight.Bold
            )
            Spacer(modifier = Modifier.height(8.dp))
            Text("Effective Date: February 13th, 2026", style = MaterialTheme.typography.bodyMedium)
            Spacer(modifier = Modifier.height(16.dp))

            Text("Welcome to Songwriter's Notebook. This policy explains what information we collect, how we use it, and how we share it.", style = MaterialTheme.typography.bodyMedium)
            Spacer(modifier = Modifier.height(16.dp))

            SectionTitle("Information We Collect")
            Text(
                "1. Account Information:\n   - When you sign in using Google, we collect your Email Address and a unique Google User ID. This is used to create your account and securely sync your data.\n\n   - User-Generated Content:\n   - Songs: We store the songs you create, which includes titles, lyrics, chord progressions, BPM, and other song metadata.\n   - Audio Recordings: We store the audio files you record within the app.\n   - Data Storage: We track your total data and audio storage usage to manage your storage quota.",
                style = MaterialTheme.typography.bodyMedium
            )
            Spacer(modifier = Modifier.height(16.dp))

            SectionTitle("How We Use Your Information")
            Text(
                "We use your information for the following purposes:\n\n- To Provide Core Features: Your account information and user-generated content are used to save your work, sync it across devices, and allow you to access it. Your intellectual property stays yours.\n- To Provide AI Features: To generate AI-powered lyric and chord suggestions, relevant song content (like lyrics and titles) and any prompts you write are sent to our secure backend services for processing.\n- To Provide Word Tools: To provide synonym suggestions, the specific word you are looking up is sent to a third-party API. Rhyme finding is processed locally on your device.",
                style = MaterialTheme.typography.bodyMedium
            )
            Spacer(modifier = Modifier.height(16.dp))

            SectionTitle("How We Share Your Information")
            Text(
                "We do not sell your personal data. We only share information with the following service providers to make the app work:\n\n- Google (Firebase): Your account information (Email, User ID), song data (Firestore), and audio recordings (Firebase Cloud Storage) are stored on Google's secure Firebase platform.\n- Google AI (Gemini): To provide AI features, song data and prompts are processed by Google's developer AI services. This data is treated as confidential, is not used to train Google's models, and is not permanently stored by the AI service.\n- Third-Party APIs: To provide the synonym feature, individual words are sent to third-party data APIs (like DataMuse). No personal information or song content is sent with this request.",
                style = MaterialTheme.typography.bodyMedium
            )
            Spacer(modifier = Modifier.height(16.dp))

            SectionTitle("Advertising and Subscriptions")
            Text("- Ads: We may implement Google AdMob to show ads in the free version of the app. AdMob may collect device identifiers to show personalized ads.\n- Subscriptions: We will use Google Play Billing to process subscriptions. We do not see or store your payment card details.",
                style = MaterialTheme.typography.bodyMedium
            )
            Spacer(modifier = Modifier.height(16.dp))

            SectionTitle("Data Security")
            Text("We use industry-standard measures (like Firebase's built-in security and HTTPS) to protect your data.", style = MaterialTheme.typography.bodyMedium)
            Spacer(modifier = Modifier.height(16.dp))

            SectionTitle("Data Deletion")
            Text("You can delete your songs and recordings from within the app at any time. To request a full deletion of your account and all associated data, please visit our Data Deletion Page at: https://songwritersnotebook-app.web.app/delete_data.html", style = MaterialTheme.typography.bodyMedium)
            Spacer(modifier = Modifier.height(16.dp))

            SectionTitle("Changes to This Policy")
            Text("We may update this policy. We will notify you of any significant changes.", style = MaterialTheme.typography.bodyMedium)
            Spacer(modifier = Modifier.height(16.dp))

            SectionTitle("Limitation of Liability")
            Text(
                "Breakwater Labs LLC provides this application on an \"as is\" basis. While we strive for perfection, software may contain bugs. To the maximum extent permitted by applicable law, Breakwater Labs LLC shall not be liable for any direct, indirect, incidental, special, consequential, or exemplary damages, including but not limited to, damages for loss of profits, goodwill, use, data, or other intangible losses, resulting from the use of or inability to use the service.\n\nIn no event shall the total liability of Breakwater Labs LLC to you for all damages, losses, or causes of action exceed the amount you have paid to Breakwater Labs LLC in the last twelve (12) months, or fifty dollars ($50.00), whichever is greater.",
                style = MaterialTheme.typography.bodyMedium
            )
            Spacer(modifier = Modifier.height(16.dp))

            SectionTitle("Contact Us")
            Text("If you have any questions about this privacy policy, please contact us at:\n\nsupport@songwritersnotebook.app", style = MaterialTheme.typography.bodyMedium)
            Spacer(modifier = Modifier.height(32.dp))
        }
    }
}

@Composable
fun SectionTitle(text: String) {
    Text(
        text,
        style = MaterialTheme.typography.titleMedium,
        fontWeight = FontWeight.Bold,
        color = MaterialTheme.colorScheme.primary,
        modifier = Modifier.padding(bottom = 8.dp)
    )
}
